// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui
import { Alert, CircularProgress } from '@mui/material';

// api
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import type { ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';
import { useGetV2AlarmTypesByIdQuery } from '@/api/generated/recalc/endpoints/v2/alarmTypes';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import { CreateQualityOfServiceDataStreamConfiguration } from '@/features/alarm-hub/components/alarm-configuration/forms/qualityOfServiceDataStream';
import { CreateUsageOverTimeConfiguration } from '@/features/alarm-hub/components/alarm-configuration/forms/usageOverTime/CreateUsageOverTimeConfiguration';

export const Route = createFileRoute(
  '/$accountId/_user/alarms/configuration/create/$alarmTypeId',
)({
  component: RouteComponent,
  staticData: { crumb: 'common:actions.create' },
});

function renderAlarmTypeForm({
  alarmTypeId,
  accountId,
  configurationSchema,
  t,
  units,
}: {
  alarmTypeId: number;
  accountId: number;
  configurationSchema?: string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
  units: ReadUnitDto[];
}) {
  switch (alarmTypeId) {
    case 24:
      return (
        <CreateUsageOverTimeConfiguration
          accountId={accountId}
          configurationSchema={configurationSchema}
          units={units}
        />
      );
    case 25:
      return (
        <CreateQualityOfServiceDataStreamConfiguration
          accountId={accountId}
          configurationSchema={configurationSchema}
          units={units}
        />
      );
    default:
      return (
        <Alert severity="info">
          {t('type.notSupported', {
            defaultValue:
              'No dedicated create form exists yet for this alarm type.',
          })}
        </Alert>
      );
  }
}

function RouteComponent() {
  const { t } = useTranslation('alarms');
  const { alarmTypeId, accountId } = Route.useParams();

  const {
    data: alarmType,
    isLoading,
    isError,
  } = useGetV2AlarmTypesByIdQuery({ id: Number(alarmTypeId) });

  const alarmTypeName = alarmType?.name
    ? t(`alarm_types.${alarmType.name}`, {
        defaultValue: alarmType.name,
      })
    : t('detail_page.unknown');

  const { data: units, isLoading: isLoadingUnits } =
    useUnitsGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${Number(accountId)})` },
      { skip: !accountId },
    );

  if (isLoading || isLoadingUnits) {
    return (
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <CircularProgress />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title={alarmTypeName} />

      {!isLoading &&
        !isError &&
        alarmType &&
        renderAlarmTypeForm({
          alarmTypeId: Number(alarmTypeId),
          accountId: Number(accountId),
          configurationSchema: alarmType.configurationSchema,
          t,
          units: units ?? [],
        })}
    </PageContainer>
  );
}
